import * as moment from 'moment';
import { Filter } from '../models/filter';

export class Filtering {
    /**
     * Filter an array of elements
     * @param {any[]} array Array to filter
     * @param {Filter[]} filters Filtering parameters
     * @returns An array containing elements matching the filters
     */
    static filter(array: any[], filters: Filter[]): any[] {
        // Undefined array
        if (!array) {
            return [];
        }

        // Empty array
        if (array.length === 0) {
            return [];
        }

        // No filters
        if (filters.length === 0) {
            return array;
        }

        return array.filter(element => {
            return Filtering._matchAll(element, filters);
        });
    }

    static _match(element: any, filter: Filter): boolean {
        if (!filter) {
            // Undefined filter
            return true;
        }
        // Check if this is a single filter or a complex filter
        else if (filter.filters.length > 0) {
            // Complex
            return Filtering._matchComplex(element, filter);
        } else {
            // Single
            return Filtering._matchSingle(element, filter);
        }
    }

    static _matchAll(element: any, filters: Filter[]): boolean {
        let match: boolean = false;

        for (var key in filters) {
            match = Filtering._match(element, filters[key]);
            if (!match) {
                return false;
            }
        }

        return true;
    }

    static _matchComplex(element: any, filter: Filter): boolean {
        // Init match indicator depending on filter operator
        let match: boolean = filter.and;
        // Loop over filters to resolve them individually
        for (var key in filter.filters) {
            match = Filtering._match(element, filter.filters[key]);
            if ((filter.and && !match) || (!filter.and && match)) {
                // 1 condition is not respected and operator is AND or
                // 1 condition is     respected and operator is OR
                // No need to go further
                break;
            }
        }

        // Check if we have a NOT operator on the filter
        match = filter.not ? !match : match;

        return match;
    }

    static _matchSingle(element: any, filter: Filter): boolean {
        let match: boolean = false;
        let value = Filtering._resolve(element, filter.property);
        if (Array.isArray(value)) {
            match = Filtering.filter(value, [filter]).length > 0;
        } else {
            // Check if condition matches
            switch (filter.comparator) {
                case Filter.COMPARATOR.EQ:
                    if (moment.isMoment(value)) {
                        match = value.isSame(filter.value);
                    } else {
                        match = value === filter.value;
                    }
                    break;
                case Filter.COMPARATOR.NE:
                    if (moment.isMoment(value)) {
                        match = !value.isSame(filter.value);
                    } else {
                        match = value !== filter.value;
                    }
                    break;
                case Filter.COMPARATOR.GE:
                    if (moment.isMoment(value)) {
                        match = value.isSameOrAfter(filter.value);
                    } else {
                        match = value >= filter.value;
                    }
                    break;
                case Filter.COMPARATOR.GT:
                    if (moment.isMoment(value)) {
                        match = value.isAfter(filter.value);
                    } else {
                        match = value > filter.value;
                    }
                    break;
                case Filter.COMPARATOR.LE:
                    if (moment.isMoment(value)) {
                        match = value.isSameOrBefore(filter.value);
                    } else {
                        match = value <= filter.value;
                    }
                    break;
                case Filter.COMPARATOR.LT:
                    if (moment.isMoment(value)) {
                        match = value.isBefore(filter.value);
                    } else {
                        match = value < filter.value;
                    }
                    break;
                case Filter.COMPARATOR.Contains:
                    match = !!value && value.toLowerCase().includes(filter.value.toLowerCase());
                    break;
                case Filter.COMPARATOR.EndsWith:
                    match = !!value && value.toLowerCase().endsWith(filter.value.toLowerCase());
                    break;
                case Filter.COMPARATOR.StartsWith:
                    match = !!value && value.toLowerCase().startsWith(filter.value.toLowerCase());
                    break;
                default:
                    throw 'Invalid filter comparator';
            }
        }

        // Check if we have a NOT operator on the filter
        match = filter.not ? !match : match;

        return match;
    }

    static _resolve(instance: any, path: string): any {
        // If no path given, then return whole object as a string
        if (!path) {
            if (typeof instance === 'string') {
                return instance;
            } else {
                return JSON.stringify(instance);
            }
        }

        path = path.replace(/\./g, '/');
        var pathParts = path.split('/');
        var current = instance;
        var next = null;
        while (pathParts.length) {
            if (typeof current !== 'object') {
                return current;
            }
            next = current[pathParts.shift() || 0];
            if (typeof next === 'function') {
                current = next.call(current);
            } else if (next === null || typeof next === 'undefined') {
                return null;
            } else if (Array.isArray(next)) {
                let values = [];
                for (let i = 0; i < next.length; i++) {
                    values.push(Filtering._resolve(next[i], pathParts.join('/')));
                }
                return values;
            } else {
                current = next;
            }
        }
        return current;
    }
}
